"use client";
import Link from "next/link";
import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface Props {
  categories: Category[];
  merchantSlug: string;
}

export default function CategorySlider({ categories, merchantSlug }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: "left" | "right") => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "left" ? -320 : 320, behavior: "smooth" });
  };

  return (
    <div className="mb-12">
      {/* header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <span className="w-1 h-5 bg-indigo-950 rounded-full" />
          <span className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-900">
            Browse Categories
          </span>
        </div>
        <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest hidden sm:block">
          {categories.length} Departments
        </p>
      </div>

      {/* slider */}
      <div className="relative group/slider">

        {/* left btn */}
        <button
          onClick={() => scroll("left")}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20
            w-9 h-9 rounded-xl bg-indigo-950 border border-indigo-800 text-white
            flex items-center justify-center shadow-lg shadow-indigo-950/20
            hover:bg-blue-600 hover:border-blue-600
            opacity-0 group-hover/slider:opacity-100
            transition-all duration-200 active:scale-90"
        >
          <ChevronLeft size={16} strokeWidth={2.5} />
        </button>

        {/* right btn */}
        <button
          onClick={() => scroll("right")}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20
            w-9 h-9 rounded-xl bg-indigo-950 border border-indigo-800 text-white
            flex items-center justify-center shadow-lg shadow-indigo-950/20
            hover:bg-blue-600 hover:border-blue-600
            opacity-0 group-hover/slider:opacity-100
            transition-all duration-200 active:scale-90"
        >
          <ChevronRight size={16} strokeWidth={2.5} />
        </button>

        {/* fades */}
        <div className="absolute left-0 top-0 h-full w-10 bg-gradient-to-r from-zinc-50 to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-zinc-50 to-transparent z-10 pointer-events-none" />

        {/* track — no overflow-hidden so chips don't clip */}
        <div
          ref={trackRef}
          className="flex flex-nowrap overflow-x-auto gap-3 py-2 px-2"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {categories.map((item, i) => (
            <Link
              key={item.id || i}
              href={`/products/${merchantSlug}/${item.slug}`}
              className="no-underline flex-shrink-0 group/cat"
            >
              <div className="flex items-center gap-2 h-10 px-5 bg-white border border-slate-200 rounded-xl
                text-[11px] font-bold text-slate-600 uppercase tracking-wider
                hover:bg-indigo-950 hover:border-indigo-950 hover:text-white
                hover:shadow-[0_6px_16px_rgba(30,27,75,0.15)] hover:-translate-y-0.5
                transition-all duration-200 active:scale-95 shadow-sm whitespace-nowrap">
                <span className="w-1 h-1 rounded-full bg-slate-300 group-hover/cat:bg-blue-400 transition-colors shrink-0" />
                {item.name}
              </div>
            </Link>
          ))}
        </div>

      </div>
    </div>
  );
}
